/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';
import { Approval, ListData, Principal } from '../../data/types.generated';
import { useApprovalsQuery } from '../../utilities/ApolloRestUtils';
import { NumberToRequestType } from '../../utilities/ApprovalTypeHelper';
import { getAppHost } from '../../utilities/TeamsUtils';
import { computeInfoTagPropsFromProperties } from '../../views/common';
import { ApprovalsAppListItem } from '../../views/components';
import ApprovalsListContext from './ApprovalsListContext';
import { ApprovalsListProviderProps } from './ApprovalsListProviderProps';

export default ({ listData, children }: ApprovalsListProviderProps): JSX.Element => {
    const updatedListData = React.useMemo((): ListData => {
        return {
            ...listData,
            excludeThumbnails: getAppHost().getEcsConfig().MicrosoftTeamApprovals.ClientFetchListProfilePics,
        };
    }, [listData]);
    const {
        data: approvalsData,
        loading,
        error,
        fetchMoreItems,
        hasMoreItems,
        refetch,
    } = useApprovalsQuery(updatedListData, 'cache-and-network');

    const config = approvalsData?.ListApprovals?.config;

    // TODO: log an error if the approval record is missing a sender or a status https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/2951536

    // Only recalculate list items when approvals data has changed, to keep the same reference possible
    const items = React.useMemo((): ApprovalsAppListItem[] => {
        if (!approvalsData?.ListApprovals?.value?.length) {
            return [];
        }

        const getUserData = (principals: Principal[], userId: string): any =>
            principals && userId
                ? principals.find((principal: Principal): boolean => principal && principal.id === userId)
                : null;
        const flowEnvironment = updatedListData?.flowEnvironment;
        const approvalType = updatedListData?.approvalType;

        const getListItem = (approval: Approval): ApprovalsAppListItem => {
            const approvalId = approval?.name;
            const templateUrl = approval?.templateUrl;
            const dataPayloadUrl = approval?.detailsUrl;
            const columns = approval?.columns;
            const properties = approval?.properties || {};
            const {
                title,
                owner,
                principals,
                approvers: approverIds,
                creationDate: creationDateString,
                category,
                source,
                priority,
                requestType: requestTypeString,
                isActive,
            } = properties;

            const infoTagProps = computeInfoTagPropsFromProperties(properties);

            const creationDate = new Date(creationDateString);
            const requestedBy = getUserData(principals, owner?.id);
            const approvers =
                approverIds && approverIds.map((approverId: string): Principal => getUserData(principals, approverId));
            return {
                approvalId,
                title,
                isActive,
                status: infoTagProps.colorStyle,
                statusText: infoTagProps.text,
                creationDate,
                source: source ?? '',
                priority,
                // category could be null. When it's null, string compare result will always be false.
                // assign category to empty string to make sorting work correctly.
                category: category ?? '',
                requestedBy,
                approvers,
                flowEnvironment,
                approvalType,
                requestType: NumberToRequestType(requestTypeString),
                templateUrl,
                dataPayloadUrl,
                columns,
            };
        };

        return approvalsData.ListApprovals.value.map(getListItem);
    }, [approvalsData, updatedListData]);

    return (
        <ApprovalsListContext.Provider value={{ loading, error, fetchMoreItems, hasMoreItems, refetch, config, items }}>
            {children}
        </ApprovalsListContext.Provider>
    );
};
